import type { WeaponCategory } from '@/config/weapons';

/**
 * In-run player stats — upgrade cards mutate these.
 * Base values live in config; this is the multiplier/bonus layer.
 */
export class PlayerStats {
  damageMul = 1;
  fireRateMul = 1;
  speedMul = 1;
  /** Magnet radius multiplier */
  magnetMul = 1;
  /** Extra supershot energy per landed hit */
  energyBonus = 0;
  /** Hearts added on top of PLAYER.maxHp */
  bonusHp = 0;
  /** Extra projectiles in the supershot fan */
  superFanBonus = 0;
  /** Extra dash invulnerability seconds (Ghost Dash card) */
  dashIframeBonus = 0;
  /** Per-category DAMAGE multiplier (docs/12 M5 — split from old catBoost) */
  catDamage: Record<WeaponCategory, number> = { bullet: 1, plasma: 1, laser: 1, bomb: 1 };
  /** Per-category FIRE RATE multiplier (separate stat, separate cards) */
  catFireRate: Record<WeaponCategory, number> = { bullet: 1, plasma: 1, laser: 1, bomb: 1 };
  /** Friendly projectile speed multiplier */
  projSpeedMul = 1;
  /** Friendly projectile lifetime (range) multiplier — base range is short */
  rangeMul = 1;
  /** Supershot damage multiplier */
  supershotMul = 1;
  /** Dash cooldown multiplier (lower = faster) */
  dashCooldownMul = 1;
  /** Post-hit invulnerability duration multiplier */
  iframeMul = 1;
  /** Shield charges granted at every arena start */
  shieldStart = 0;
  /** Hull cells repaired at every arena start (Nano Swarm card) */
  regenCells = 0;
  /** Yellow gem income multiplier */
  greedMul = 1;
  /** Shop price multiplier (lower = cheaper) */
  discountMul = 1;
  /** Lucky Chip card: reroll price never escalates */
  rerollLock = false;
  /** Reroll Voucher stacks → tiered price cut (docs/12 I6) */
  rerollDiscountSteps = 0;
  /** Plating Deal card: yellow armor-buy price multiplier (docs/12 O) */
  armorPriceMul = 1;
  /** Extra weapon inventory slots (Cargo Bay card) */
  cargoSlots = 0;
  /** Added on top of PLAYER.crit.baseChance */
  critChanceBonus = 0;
  /** Added on top of PLAYER.crit.baseDamageMul */
  critDamageBonus = 0;
  /** Life Drinker card: fraction of damage dealt returned as hull HP */
  lifestealPct = 0;
  /** Incoming HP damage multiplier (Dampening Field card — lower is better) */
  damageTakenMul = 1;
  /** Armor units gained on every boss kill (Salvage Rig card) */
  armorOnKill = 0;
  /** Aegis Link card: damage reduction scales with the damage bonus */
  aegisLink = false;

  reset(): void {
    this.damageMul = 1;
    this.fireRateMul = 1;
    this.speedMul = 1;
    this.magnetMul = 1;
    this.energyBonus = 0;
    this.bonusHp = 0;
    this.superFanBonus = 0;
    this.dashIframeBonus = 0;
    this.catDamage = { bullet: 1, plasma: 1, laser: 1, bomb: 1 };
    this.catFireRate = { bullet: 1, plasma: 1, laser: 1, bomb: 1 };
    this.projSpeedMul = 1;
    this.rangeMul = 1;
    this.supershotMul = 1;
    this.dashCooldownMul = 1;
    this.iframeMul = 1;
    this.shieldStart = 0;
    this.regenCells = 0;
    this.greedMul = 1;
    this.discountMul = 1;
    this.rerollLock = false;
    this.rerollDiscountSteps = 0;
    this.armorPriceMul = 1;
    this.cargoSlots = 0;
    this.critChanceBonus = 0;
    this.critDamageBonus = 0;
    this.lifestealPct = 0;
    this.damageTakenMul = 1;
    this.armorOnKill = 0;
    this.aegisLink = false;
  }
}

/** Deep-enough clone for card previews (category maps are the nested fields). */
export function cloneStats(s: PlayerStats): PlayerStats {
  const c = new PlayerStats();
  Object.assign(c, s);
  c.catDamage = { ...s.catDamage };
  c.catFireRate = { ...s.catFireRate };
  return c;
}
